import type {
  SmsCharInfo,
  SmsPhonePreview,
  SmsPreviewToken,
  SmsSampleRendering,
  SmsSmartTag,
} from './types';

/** Default Hard-text body the composer opens with. */
export const SMS_DEFAULT_BODY =
  'Hi {{first_name}}, we missed you at Smile Dental today. No worries — pick a new time that works for you here: {{booking_link}} Reply STOP to opt out.';

/** Default AI prompt the AI-text mode opens with. */
export const SMS_DEFAULT_PROMPT =
  "Write a short, warm text to a patient who missed today's appointment. Reassure them it's no problem, invite them to rebook with the link, keep it under 160 characters. Always end with the STOP opt-out.";

/** Smart-tag chips the author can insert at the cursor (Hard-text mode). */
export const SMS_SMART_TAGS: SmsSmartTag[] = [
  { id: 'first_name', token: '{{first_name}}', label: '{{first_name}}', sample: 'Sarah', kind: 'merge' },
  { id: 'provider', token: '{{provider}}', label: '{{provider}}', sample: 'Dr. Patel', kind: 'merge' },
  {
    id: 'last_visit_date',
    token: '{{last_visit_date}}',
    label: '{{last_visit_date}}',
    sample: 'Jan 14',
    kind: 'merge',
  },
  {
    id: 'booking_link',
    token: '{{booking_link}}',
    label: 'Booking link',
    sample: 'elva.to/r/8kQ2',
    kind: 'link',
    icon: 'link',
  },
  {
    id: 'survey_link',
    token: '{{survey_link}}',
    label: 'Survey link',
    sample: 'elva.to/s/4mN9',
    kind: 'link',
    icon: 'check-circ',
  },
];

/** Sample AI renderings (preview-only — actual copy is generated at send time). */
export const SMS_SAMPLE_RENDERINGS: SmsSampleRendering[] = [
  {
    id: 'sample-1',
    label: 'Sample 1',
    text: "Hi Sarah! Sorry we missed you today — life happens. Grab a new time whenever you're ready: elva.to/r/8kQ2 Reply STOP to opt out.",
  },
  {
    id: 'sample-2',
    label: 'Sample 2',
    text: "Hey Sarah, no worries about today's visit! Pick a slot that works for you here: elva.to/r/8kQ2 Text STOP anytime to opt out.",
  },
  {
    id: 'sample-3',
    label: 'Sample 3 · preferred language',
    lang: 'ES',
    text: 'Hola Sarah, no pasa nada por la cita de hoy. Reserva un nuevo horario aquí: elva.to/r/8kQ2 Responde STOP para darte de baja.',
  },
];

/** AI-mode sub-label next to the "AI prompt" badge. */
export const SMS_AI_PROMPT_NOTE = 'copy generated per-recipient at send time';

/** AI-mode note under the "Sample renderings" heading. */
export const SMS_SAMPLES_NOTE =
  'Preview only — actual copy is generated at send time, fresh for each patient.';

/** Locked-facts guardrail strip shown in AI mode. */
export const SMS_LOCKED_NOTE = {
  lead: 'Approved = prompt + locked facts + guardrails.',
  rest: 'ELVA may vary wording and translate, but never changes the offer or drops the STOP opt-out.',
} as const;

/** "+1-only" honest note shown in both modes. */
export const SMS_PLUS_ONE_NOTE = {
  lead: '+1 (US/Canada) numbers only.',
  rest: 'SMS sends only to +1 numbers — any other number is skipped at send and shown in your audience summary.',
} as const;

/**
 * Token → sample-value map for rendering the phone preview and estimating the
 * rendered length. Derived from the smart tags so the two stay in sync.
 */
export const SMS_PREVIEW_TOKENS: SmsPreviewToken[] = SMS_SMART_TAGS.map((tag) => ({
  token: tag.token,
  value: tag.sample,
  kind: tag.kind,
}));

/** Right-hand phone preview content. */
export const SMS_PHONE_PREVIEW: SmsPhonePreview = {
  sender: 'Smile Dental',
  number: '+1 (512) 555-0148',
  timestamp: 'Today 9:14 AM',
  body: SMS_DEFAULT_BODY,
  linkNote: 'Tracked short link',
  renderedFor: 'Sarah M.',
  category: 'transactional · reminder',
};

/** Pre-computed char / segment readout for the default Hard-text body. */
export const SMS_DEFAULT_CHAR_INFO: SmsCharInfo = {
  chars: 142,
  segments: 1,
  budget: 160,
  state: 'warn',
};
